import { useCallback } from 'react'
import { projectApi } from '@/api/projectApi'
import { applyIncrementalImport, type IncrementalImportOptions, type IncrementalImportResult } from '@/services/incrementalImportService'
import { applyImportLayout, type SqlImportSummary } from '@/services/sqlImportService'
import { useDiagramStore, useHistoryStore, useViewportStore } from '@/stores'
import { useDraftStore } from '@/stores/draftStore'
import { useTabViewStore } from '@/stores/tabViewStore'
import type { DatabaseDialect, DiagramSnapshot, ProjectTabSnapshot } from '@/types'

function buildCurrentDiagramSnapshot(): DiagramSnapshot {
  const state = useDiagramStore.getState()
  return {
    tables: state.tables,
    rels: state.rels,
    groups: state.groups,
    defaults: state.defaults,
    projectName: state.projectName,
    dialect: state.dialect,
    hiddenTableIds: useTabViewStore.getState().hiddenTableIds,
  }
}

function ensureHistoryBaseline(snapshot: DiagramSnapshot) {
  const history = useHistoryStore.getState()
  if (history.undoStack.length === 0) {
    history.initHistory(snapshot)
  }
}

function scheduleResetView(tables: ProjectTabSnapshot['tables']) {
  requestAnimationFrame(() => {
    useViewportStore.getState().resetView(tables)
  })
}

function normalizeHiddenTableIds(hiddenTableIds: string[], snapshot: ProjectTabSnapshot) {
  const availableTableIds = new Set(snapshot.tables.map((table) => table.id))
  return hiddenTableIds.filter((tableId) => availableTableIds.has(tableId))
}

function replaceActiveDiagram(snapshot: ProjectTabSnapshot, label: string, hiddenTableIds?: string[]) {
  ensureHistoryBaseline(buildCurrentDiagramSnapshot())
  const nextHiddenTableIds = normalizeHiddenTableIds(
    hiddenTableIds ?? useTabViewStore.getState().hiddenTableIds,
    snapshot,
  )
  useTabViewStore.getState().setHiddenTableIds(nextHiddenTableIds)
  useDiagramStore.getState().loadDiagram(snapshot)
  useHistoryStore.getState().commitHistory({
    tables: snapshot.tables,
    rels: snapshot.rels,
    groups: snapshot.groups,
    defaults: snapshot.defaults,
    projectName: snapshot.projectName,
    dialect: snapshot.dialect,
    hiddenTableIds: nextHiddenTableIds,
  }, label)
  scheduleResetView(snapshot.tables)
}

function buildImportedSnapshot(input: {
  tables: ProjectTabSnapshot['tables']
  rels: ProjectTabSnapshot['rels']
  dialect: DatabaseDialect
  projectName: string
}): ProjectTabSnapshot {
  return {
    tables: input.tables,
    rels: input.rels,
    groups: {},
    defaults: useDiagramStore.getState().defaults,
    projectName: input.projectName,
    dialect: input.dialect,
  }
}

export function useImportActions() {
  const tables           = useDiagramStore(state => state.tables)
  const rels             = useDiagramStore(state => state.rels)
  const groups           = useDiagramStore(state => state.groups)

  const hasProjectContent = useCallback(() => {
    return tables.length > 0 || rels.length > 0 || Object.keys(groups).length > 0
  }, [groups, rels.length, tables.length])

  // ── Atualiza o projeto aberto a partir de SQL (parsing no backend, dialeto fixo) ──
  const previewIncrementalSqlImport = useCallback(async (input: {
    sql: string
    options: IncrementalImportOptions
  }): Promise<IncrementalImportResult & { summary: SqlImportSummary }> => {
    const projectId = useDraftStore.getState().currentProjectId
    if (!projectId) throw new Error('NO_PROJECT')

    const parsed = applyImportLayout(await projectApi.importSqlParse(projectId, { sql: input.sql }))

    const currentProject: ProjectTabSnapshot = {
      tables: useDiagramStore.getState().tables,
      rels: useDiagramStore.getState().rels,
      groups: useDiagramStore.getState().groups,
      defaults: useDiagramStore.getState().defaults,
      projectName: useDiagramStore.getState().projectName,
      dialect: useDiagramStore.getState().dialect,
    }

    // The project dialect is immutable, so the imported model inherits it.
    const importedProject = buildImportedSnapshot({
      tables: parsed.tables,
      rels: parsed.rels,
      dialect: useDiagramStore.getState().dialect,
      projectName: currentProject.projectName,
    })

    return {
      ...applyIncrementalImport(currentProject, importedProject, input.options, parsed.warnings),
      summary: parsed.summary,
    }
  }, [])

  const applyIncrementalSqlImport = useCallback((result: IncrementalImportResult) => {
    replaceActiveDiagram(
      result.project,
      'import-sql:incremental',
      useTabViewStore.getState().hiddenTableIds,
    )
    return result
  }, [])

  return {
    hasProjectContent,
    previewIncrementalSqlImport,
    applyIncrementalSqlImport,
  }
}
